import React, { useRef, useEffect, useMemo } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Image,
  Animated,
  Dimensions,
  StyleSheet,
  ScrollView,
  Platform,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppImages } from "../../../../utility/AppImages";
import { CommonButton } from "../../../../common/CommonButton";
import { useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppScreens } from "../../../Utility/AppScreens";

const { height: screenHeight } = Dimensions.get("window");

interface WinnerBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  eventDetails: any;
  winnerDetails: any;
}

const WinnerBottomSheet: React.FC<WinnerBottomSheetProps> = ({
  visible,
  onClose,
  eventDetails,
  winnerDetails,
}) => {
  const navigation = useNavigation();
  const slideAnim = useRef(new Animated.Value(screenHeight)).current;

  // Belt exchange rates (credits per belt)
  const beltCreditRates = {
    Bronze: 500,
    Silver: 5000,
    Gold: 50000,
    Platinum: 500000,
  };

  // Belt sell rates (dollar worth per belt)
  const beltSellRates = {
    Bronze: 5,
    Silver: 50,
    Gold: 500,
    Platinum: 5000,
  };

  const medalsData = useMemo(() => {
    const values: Record<string, number> = {
      Coins: 0,
      Bronze: 0,
      Silver: 0,
      Gold: 0,
      Platinum: 0,
    };
    (eventDetails?.stakes || []).forEach((stake: any) => {
      if (
        stake?.type &&
        typeof stake.quantity === "number" &&
        stake.quantity >= 0
      ) {
        values[stake.type] = stake.quantity;
      }
    });
    return [
      { id: 1, name: "Coin", image: AppImages.icCoin, value: values.Coins },
      {
        id: 2,
        name: "Bronze",
        image: AppImages.icBronzeBelt,
        value: values.Bronze,
      },
      {
        id: 3,
        name: "Silver",
        image: AppImages.icSilverBelt,
        value: values.Silver,
      },
      { id: 4, name: "Gold", image: AppImages.icGoldBelt, value: values.Gold },
      {
        id: 5,
        name: "Platinum",
        image: AppImages.icPlatinumBelt,
        value: values.Platinum,
      },
    ].filter((medal) => medal.value > 0);
  }, [eventDetails?.stakes]);

  // Calculate belt credits worth
  const beltCreditsWorth = useMemo(() => {
    let totalCredits = 0;
    let totalWorth = 0;
    let beltDescription = "";

    medalsData.forEach((medal) => {
      if (medal.name !== "Coin") {
        const creditValue =
          beltCreditRates[medal.name as keyof typeof beltCreditRates] || 0;
        const sellValue =
          beltSellRates[medal.name as keyof typeof beltSellRates] || 0;

        totalCredits += medal.value * creditValue;
        totalWorth += medal.value * sellValue;

        if (medal.value > 0) {
          beltDescription += beltDescription ? ", " : "";
          beltDescription += `${medal.value} ${medal.name} Belt${
            medal.value > 1 ? "s" : ""
          }`;
        }
      }
    });

    return { totalCredits, totalWorth, beltDescription };
  }, [medalsData]);

  useEffect(() => {
    if (visible) {
      // Slide up when visible
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 10,
      }).start();
    } else {
      // Slide down when not visible
      Animated.timing(slideAnim, {
        toValue: screenHeight,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible]);

  return (
    <Modal transparent={true} visible={visible} animationType="none">
      <View style={styles.overlay}>
        {/* Background overlay - not touchable to prevent closing */}
        <View style={styles.backdrop} />

        {/* Bottom sheet */}
        <Animated.View
          style={[
            styles.bottomSheet,
            {
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {/* Handle bar */}
          <View style={styles.handleBar} />

          {/* Fixed Header - Winner */}
          <View>
            <View style={styles.headerSection}>
              <Text style={styles.winnerText}>Winner</Text>
              <TouchableOpacity
                onPress={() => {
                  navigateToScreen(navigation, AppScreens.Home);
                  onClose();
                }}
                style={styles.closeButton}
              >
                <Image
                  source={AppImages.icCancel}
                  style={styles.closeIcon}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>
            {/* Separator */}
            <View style={styles.separator} />
          </View>

          {/* Scrollable Middle Content */}
          <ScrollView
            showsVerticalScrollIndicator={false}
            bounces={false}
            style={styles.scrollableContent}
            contentContainerStyle={styles.scrollContentContainer}
          >
            {/* Green box with winner name */}
            <View style={styles.winnerNameBox}>
              <Text style={styles.winnerNameText}>
                {winnerDetails?.username || "Unknown"} Won
              </Text>
            </View>

            {/* User and Credits Row */}
            <View style={styles.mainContentRow}>
              {/* Left: User info card */}
              <LinearGradient
                colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.userCard}
              >
                <View style={styles.userImageContainer}>
                  <View style={styles.avatarCircle}>
                    <Image
                      source={{
                        uri:
                          winnerDetails?.profilePicture ||
                          "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg",
                      }}
                      style={styles.avatarImage}
                      resizeMode="contain"
                    />
                  </View>
                </View>
                <Text style={styles.usernameText}>
                  {winnerDetails?.username || "Unknown"}
                </Text>
              </LinearGradient>

              {/* Right: Credits and Belts */}
              <View style={styles.creditsContainer}>
                <View style={styles.creditsRow}>
                  <Text style={styles.creditsValue}>
                    {winnerDetails?.credit ?? 0}
                  </Text>
                  <Text style={styles.giftCreditsLabel}>Gift Credits</Text>
                </View>

                {/* Belts */}
                <View style={styles.beltsView}>
                  {medalsData.map((medal) => (
                    <View key={medal.id} style={styles.beltItem}>
                      <Image
                        //@ts-ignore
                        source={medal?.image}
                        style={styles.beltImage}
                      />
                      <Text style={styles.beltValue}>{medal.value}</Text>
                      <Text style={styles.beltName}>{medal.name}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>

            {/* Belt Worth and Pound Row */}
            {beltCreditsWorth.totalCredits > 0 && (
              <View style={styles.worthRow}>
                <Text style={styles.beltWorthText}>
                  {beltCreditsWorth.beltDescription}:{" "}
                  <Text style={styles.beltWorthBold}>
                    worth {beltCreditsWorth.totalCredits} credits
                  </Text>
                </Text>
                <View style={styles.poundBox}>
                  <Text style={styles.poundText}>
                    ${beltCreditsWorth.totalWorth}
                  </Text>
                </View>
              </View>
            )}
          </ScrollView>

          {/* Fixed Footer - Buttons Section */}
          <View style={styles.footerSection}>
            <View style={styles.separator} />
            <View style={styles.buttonContainer}>
              {/* Hidden "Go to event" button */}
              {/* <CommonButton
                title="Go to event"
                onTap={() => {
                  navigateToScreen(navigation, AppScreens.PastEvent, {
                    eventID: eventDetails?.eventID,
                  });
                  onClose();
                }}
                buttonStyle={styles.btnGoToEvent}
                textStyle={[AppFontStyle.BOLD_ANYBODY_14, {}]}
              /> */}
              <LinearGradient
                colors={["#DC2626", "#991B1B"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.btnFeedbackGradient}
              >
                <TouchableOpacity
                  style={styles.btnFeedback}
                  onPress={() => {
                    // Open WhatsApp link in new tab
                    if (Platform) {
                      // @ts-ignore
                      window.open('https://chat.whatsapp.com/J6ntLXPLDpYAeSmSQ7AgTG?mode=wwt', '_blank');
                    }
                  }}
                >
                  <Text style={[AppFontStyle.BOLD_ANYBODY_14, {}]}>
                    Give us feedback
                  </Text>
                </TouchableOpacity>
              </LinearGradient>
            </View>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "flex-end",
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  bottomSheet: {
    backgroundColor: "#111827",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: screenHeight * 0.6, // 60% of screen height
    borderWidth: 1.6,
    borderColor: "rgba(239, 68, 68, 0.50)",
    borderBottomWidth: 0,
  },
  handleBar: {
    width: 40,
    height: 4,
    backgroundColor: "#4B5563",
    borderRadius: 2,
    alignSelf: "center",
    marginTop: 8,
    marginBottom: 12,
  },
  headerSection: {
    paddingHorizontal: 20,
    paddingBottom: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  winnerText: {
    ...AppFontStyle.BOLD_ANYBODY_16,
    color: "#FFF",
    marginBottom: 8,
  },
  closeButton: {
    backgroundColor: "#393939",
    width: 28,
    height: 28,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 8,
  },
  closeIcon: {
    height: 12,
    width: 12,
  },
  separator: {
    height: 1,
    backgroundColor: AppColors.grayColor_333333,
  },
  scrollableContent: {
    flex: 1,
  },
  scrollContentContainer: {
    paddingHorizontal: 20,
    paddingTop: 16,
    gap: 12,
  },
  winnerNameBox: {
    height: 46,
    paddingVertical: 12,
    paddingHorizontal: 24,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: AppColors.appGreenColor_51AF07,
    borderRadius: 7,
  },
  winnerNameText: {
    ...AppFontStyle.BOLD_ANYBODY_18,
    fontWeight: "700",
    color: "#FFF",
  },
  mainContentRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "stretch",
    marginTop: 8,
  },
  userCard: {
    width: 94,
    height: 101,
    padding: 7.622,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 10.162,
    borderRadius: 6.775,
    borderWidth: 0.677,
    borderColor: "#374151",
    marginTop: 4,
  },
  userImageContainer: {
    width: 61.553,
    height: 61.553,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarCircle: {
    width: 61.553,
    height: 61.553,
    borderRadius: 30.776,
    overflow: "hidden",
    backgroundColor: "#1F2937",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarImage: {
    width: 61.553,
    height: 61.553,
  },
  usernameText: {
    ...AppFontStyle.BOLD_ANYBODY_10,
    textAlign: "center",
    color: "#FFF",
  },
  creditsContainer: {
    flex: 1,
    gap: 8,
    justifyContent: "space-between",
  },
  creditsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  creditsValue: {
    ...AppFontStyle.BOLD_ANYBODY_22,
    fontWeight: "800",
    color: "#FFF",
  },
  giftCreditsLabel: {
    ...AppFontStyle.BOLD_ANYBODY_14,
    color: "#FFF",
  },
  beltsView: {
    backgroundColor: "rgba(31, 41, 55, 0.50)",
    borderColor: "#374151",
    borderWidth: 0.73,
    borderRadius: 7.296,
    minHeight: 70,
    paddingVertical: 8,
    paddingHorizontal: 10,
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center",
    flex: 1,
  },
  beltItem: {
    alignItems: "center",
    flex: 1,
  },
  beltImage: {
    width: 28,
    height: 28,
    resizeMode: "contain",
  },
  beltValue: {
    ...AppFontStyle.BOLD_ANYBODY_12,
    marginTop: 3,
    color: "#FFF",
  },
  beltName: {
    ...AppFontStyle.REGULAR_ANYBODY_10,
    marginTop: 2,
    color: "#FFF",
  },
  worthRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  beltWorthText: {
    ...AppFontStyle.REGULAR_ANYBODY_13,
    flex: 1,
    color: "#FFF",
  },
  beltWorthBold: {
    ...AppFontStyle.BOLD_ANYBODY_14,
    fontWeight: "700",
    color: "#FFF",
  },
  poundBox: {
    backgroundColor: "#374151",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    minWidth: 80,
    alignItems: "center",
  },
  poundText: {
    ...AppFontStyle.BOLD_ANYBODY_22,
    fontWeight: "700",
    color: "#FFF",
  },
  footerSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
    marginTop: 12,
    justifyContent: "flex-end",
  },
  btnFeedback: {
    height: 40,
    paddingVertical: 8,
    paddingHorizontal: 16,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 6,
    width: "100%",
  },
  btnFeedbackGradient: {
    borderRadius: 6,
    height: 40,
    justifyContent: "center",
    alignItems: "center",
    minWidth: 180,
  },
  btnGoToEventGradient: {
    borderRadius: 6,
    height: 40,
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  btnGoToEvent: {
    height: 40,
    paddingVertical: 8,
    paddingHorizontal: 16,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
});

export default WinnerBottomSheet;
